import { supabase } from './supabase';
import logger from '../utils/logger';

// Auto-detect API URLs based on environment
const getApiUrls = () => {
  // Explicit env vars take precedence
  if (process.env.REACT_APP_CARTOON_API_URL && process.env.REACT_APP_3D_API_URL) {
    return {
      cartoon: process.env.REACT_APP_CARTOON_API_URL,
      model3d: process.env.REACT_APP_3D_API_URL,
    };
  }

  // Auto-detect based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      cartoon: 'http://localhost:8000/generate-cartoon',
      model3d: 'http://localhost:8001/generate-3d',
    };
  }

  // Production default (GCP Cloud Run)
  return {
    cartoon: 'https://tiniego-generate-cartoon-1099497855655.us-central1.run.app/generate-cartoon',
    model3d: 'https://tiniego-generate-3d-1099497855655.us-central1.run.app/generate-3d',
  };
};

const API_URLS = getApiUrls();
const CARTOON_API_URL = API_URLS.cartoon;
const MODEL3D_API_URL = API_URLS.model3d;

// API timeouts in milliseconds
const FIGURINE_TIMEOUT = parseInt(process.env.REACT_APP_FIGURINE_TIMEOUT || '120000', 10);
const MODEL_3D_TIMEOUT = parseInt(process.env.REACT_APP_3D_MODEL_TIMEOUT || '300000', 10);

// Polling configuration (exported for use in components)
export const POLLING_INTERVAL_MS = 5000; // 5 seconds between polls
export const MAX_POLL_ATTEMPTS = 60; // Max 60 attempts (5 minutes total with 5s interval)

// In-memory cache for signed URLs to reduce repeated signing and enable better caching
const signedUrlCache = new Map();
const makeCacheKey = (path, transform) => JSON.stringify({ path, transform });

// Cleanup expired cache entries periodically
const cleanupExpiredCacheEntries = () => {
  const now = Date.now();
  for (const [key, value] of signedUrlCache.entries()) {
    if (value.expiresAt <= now) {
      signedUrlCache.delete(key);
      logger.debug('Removed expired signed URL from cache:', key);
    }
  }
};

// Run cleanup every minute
if (typeof window !== 'undefined') {
  setInterval(cleanupExpiredCacheEntries, 60000);
}

export const getSignedUrlCached = async (fileName, { expiresIn = 86400, transform } = {}) => {
  try {
    const key = makeCacheKey(fileName, transform);
    const now = Date.now();
    const cached = signedUrlCache.get(key);
    if (cached && cached.expiresAt > now + 5000) {
      return { success: true, url: cached.url };
    }

    // Pass transform options to leverage Supabase image resizing for previews
    const options = transform ? { transform } : undefined;
    const { data, error } = await supabase.storage
      .from('memory-photos')
      .createSignedUrl(fileName, expiresIn, options);

    if (error) throw error;

    const expiresAt = now + expiresIn * 1000;
    signedUrlCache.set(key, { url: data.signedUrl, expiresAt });
    return { success: true, url: data.signedUrl };
  } catch (error) {
    logger.error('Error creating signed URL:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to get signed URL for private storage
export const getSignedUrl = async (fileName, expiresIn = 3600) => {
  return getSignedUrlCached(fileName, { expiresIn });
};

// Helper function to extract file path from storage URL
export const extractFilePathFromUrl = (url) => {
  if (!url) return null;
  
  // Handle both public and signed URLs
  const publicMatch = url.match(/\/storage\/v1\/object\/public\/memory-photos\/(.+)/);
  const signedMatch = url.match(/\/storage\/v1\/object\/sign\/memory-photos\/(.+)/);
  
  if (publicMatch) return (publicMatch[1] || '').split('?')[0];
  if (signedMatch) return (signedMatch[1] || '').split('?')[0];
  
  // If it's already a file path (no domain), return as is
  if (url.includes('/') && !url.includes('http')) {
    return url;
  }
  
  return null;
};

// Helper function to get display URL for images (signed URL for private storage)
export const getDisplayUrl = async (url, options = {}) => {
  if (!url) return null;
  
  // External URLs (non-Supabase storage) can be returned as-is
  if (url.includes('http') && !url.includes('/storage/v1/object/')) {
    return url;
  }
  
  // If it's already a signed Supabase URL and no transform requested, reuse it
  if (url.includes('/storage/v1/object/sign/') && !options.transform) {
    return url;
  }

  // For Supabase storage URLs or raw storage paths, resolve to a cached signed URL
  const filePath = extractFilePathFromUrl(url) || url;
  if (filePath) {
    const signedUrlResult = await getSignedUrlCached(filePath, {
      expiresIn: options.expiresIn ?? 86400,
      transform: options.transform,
    });
    if (signedUrlResult.success) {
      return signedUrlResult.url;
    }
  }
  
  // Fallback to original URL if signing fails
  return url;
};

// Photo upload function
export const uploadPhoto = async (file, userId) => {
  try {
    // Create a unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}.${fileExt}`;
    const ext = (fileExt || '').toLowerCase();
    const inferredContentType =
      file.type ||
      (ext === 'jpg' || ext === 'jpeg'
        ? 'image/jpeg'
        : ext === 'png'
          ? 'image/png'
          : ext === 'webp'
            ? 'image/webp'
            : 'application/octet-stream');

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage.from('memory-photos').upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: inferredContentType,
    });

    if (error) throw error;

    // Get signed URL for private storage
    const signedUrlResult = await getSignedUrl(fileName);
    if (!signedUrlResult.success) {
      throw new Error(signedUrlResult.error);
    }

    return { success: true, url: signedUrlResult.url, fileName, filePath: fileName };
  } catch (error) {
    logger.error('Photo upload error:', error);
    return { success: false, error: error.message };
  }
};

// Figurine image upload function
export const uploadFigurineImage = async (imageUrl, userId, memoryId) => {
  try {
    // Fetch the image from the URL
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }

    const blob = await response.blob();

    // Create a unique filename for the figurine
    const fileExt = 'png'; // Assuming PNG format for generated figurines
    const fileName = `${userId}/figurines/${memoryId}_${Date.now()}.${fileExt}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage.from('memory-photos').upload(fileName, blob, {
      cacheControl: '3600',
      upsert: false,
      contentType: blob.type || 'image/png',
    });

    if (error) throw error;

    // Get signed URL for private storage
    const signedUrlResult = await getSignedUrl(fileName);
    if (!signedUrlResult.success) {
      throw new Error(signedUrlResult.error);
    }

    return { success: true, url: signedUrlResult.url, fileName, filePath: fileName };
  } catch (error) {
    logger.error('Figurine upload error:', error);
    return { success: false, error: error.message };
  }
};

// 3D model upload function
export const upload3DModel = async (modelUrl, userId, memoryId) => {
  try {
    // Fetch the 3D model from the URL
    const response = await fetch(modelUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch 3D model');
    }

    const blob = await response.blob();

    // Create a unique filename for the 3D model
    const fileExt = 'stl'; // Assuming STL format for generated 3D models
    const fileName = `${userId}/3d-models/${memoryId}_${Date.now()}.${fileExt}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage.from('memory-photos').upload(fileName, blob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'model/stl',
    });

    if (error) throw error;

    // Get signed URL for private storage
    const signedUrlResult = await getSignedUrl(fileName);
    if (!signedUrlResult.success) {
      throw new Error(signedUrlResult.error);
    }

    return { success: true, url: signedUrlResult.url, fileName, filePath: fileName };
  } catch (error) {
    logger.error('3D model upload error:', error);
    return { success: false, error: error.message };
  }
};

// Memory CRUD operations
export const createMemory = async (memoryData) => {
  const { data, error } = await supabase.from('memories').insert([memoryData]).select().single();

  if (error) throw error;
  return data;
};

export const getUserMemories = async () => {
  const { data, error } = await supabase
    .from('memories')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getMemoryById = async (id) => {
  const { data, error } = await supabase.from('memories').select('*').eq('id', id).single();

  if (error) throw error;
  return data;
};

export const updateMemory = async (id, updates) => {
  const { data, error } = await supabase
    .from('memories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateMemoryFigurine = async (id, figurineUrl) => {
  const { data, error } = await supabase
    .from('memories')
    .update({ figurine_url: figurineUrl })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateMemory3DModel = async (id, model3dUrl) => {
  const { data, error } = await supabase
    .from('memories')
    .update({ model_3d_url: model3dUrl })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateMemoryStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('memories')
    .update({ status })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Figurine generation using direct cartoon API call
export const generateFigurine = async (photoUrl, memoryId, userId, title, description) => {
  logger.debug('Generating figurine:', photoUrl, memoryId, userId, title, description);
  try {
    const API_URL = CARTOON_API_URL;

    // Handle signed URLs for private storage
    let imageUrl = photoUrl;
    
    // If photoUrl is a storage path, get a signed URL
    const filePath = extractFilePathFromUrl(photoUrl);
    if (filePath) {
      const signedUrlResult = await getSignedUrl(filePath);
      if (signedUrlResult.success) {
        imageUrl = signedUrlResult.url;
        logger.debug('Using signed URL for image access:', imageUrl);
      } else {
        logger.warn('Failed to get signed URL, using original URL:', signedUrlResult.error);
      }
    }

    // Fetch the image from the URL (signed or original)
    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`);
    }

    const imageBlob = await imageResponse.blob();

    // Create FormData for the cartoon API
    const formData = new FormData();
    formData.append('image', imageBlob, 'image.jpg');
    formData.append('user_id', userId);
    formData.append('memory_id', memoryId);

    logger.debug([...formData.entries()]);

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FIGURINE_TIMEOUT);

    // Call the cartoon API directly
    const response = await fetch(API_URL, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Try to extract error details from response body
      let errorDetail = response.statusText;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorData.message || errorData.error || response.statusText;
      } catch {
        // If we can't parse JSON, use status text
      }
      throw new Error(`Cartoon generation failed: ${errorDetail}`);
    }

    // Check if the response is an image (PNG) or JSON
    const contentType = response.headers.get('content-type');
    logger.debug('Response content type:', contentType);

    let cartoonUrl;
    let cartoonImageBase64 = null;
    let filename = null;

    const data = await response.json();

    if (data.status !== 'success') {
      throw new Error(data.message || data.detail || 'Failed to generate cartoon');
    }

    cartoonUrl = data.cartoon_url;
    filename = data.filename;

    if (!cartoonUrl) {
      throw new Error('No cartoon URL returned from API');
    }

    return {
      success: true,
      figurine_url: cartoonUrl,
      filename: filename,
    };
  } catch (error) {
    logger.error('Figurine generation error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// 3D model generation using Cloud Run API with extended timeout
export const generate3DModelFromApi = async (userId, memoryId, enablePbr = false) => {
  try {
    const API_URL = MODEL3D_API_URL;

    const formData = new FormData();
    if (userId) formData.append('user_id', userId);
    if (memoryId) formData.append('memory_id', memoryId);
    formData.append('enable_pbr', enablePbr ? 'true' : 'false');

    // Debug logging
    logger.debug('3D API call parameters:', {
      userId,
      memoryId,
      enablePbr,
      formDataEntries: [...formData.entries()]
    });

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MODEL_3D_TIMEOUT);

    const response = await fetch(API_URL, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Try to extract error details from response body
      let errorDetail = response.statusText;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorData.message || errorData.error || response.statusText;
      } catch {
        // If we can't parse JSON, use status text
      }
      throw new Error(`3D model generation failed: ${errorDetail}`);
    }

    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || data.detail || 'Failed to generate 3D model');
    }

    const { stl_url, filename, updated_memory } = data;
    if (!stl_url) {
      throw new Error('No stl_url returned from API');
    }

    return {
      success: true,
      stl_url,
      filename,
      updated_memory,
    };
  } catch (error) {
    logger.error('3D model generation error:', error);

    return {
      success: false,
      error: error.message,
    };
  }
};

export const deleteMemory = async (id) => {
  const { error } = await supabase.from('memories').delete().eq('id', id);

  if (error) throw error;
  return true;
};

// Cart operations
export const addToCart = async (memoryId, quantity = 1, price, userId) => {
  const { data, error } = await supabase
    .from('cart_items')
    .insert([
      {
        user_id: userId,
        memory_id: memoryId,
        quantity,
        price,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getCartItems = async () => {
  const { data, error } = await supabase
    .from('cart_items')
    .select(
      `
      *,
      memories (
        id,
        title,
        description,
        photo_url,
        figurine_url,
        figurine_urls,
        price,
        height
      )
    `
    )
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const updateCartItemQuantity = async (id, quantity) => {
  const { data, error } = await supabase
    .from('cart_items')
    .update({ quantity })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeFromCart = async (id) => {
  const { error } = await supabase.from('cart_items').delete().eq('id', id);

  if (error) throw error;
  return true;
};

// Update memory height
export const updateMemoryHeight = async (memoryId, height) => {
  const { data, error } = await supabase
    .from('memories')
    .update({ height })
    .eq('id', memoryId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Order operations
export const createOrder = async (orderData) => {
  const { data, error } = await supabase.from('orders').insert([orderData]).select().single();

  if (error) throw error;
  return data;
};

export const getUserOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select(
      `
      *,
      order_items (
        *,
        memories (
          id,
          title,
          photo_url,
          figurine_url,
          figurine_urls
        )
      )
    `
    )
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const updateOrderStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Check if figurine_url is available for a memory
export const checkMemoryFigurineStatus = async (memoryId) => {
  const { data, error } = await supabase
    .from('memories')
    .select('id, figurine_url, figurine_urls, status')
    .eq('id', memoryId)
    .single();

  if (error) throw error;
  return data;
};

// Check memory status and details
export const checkMemoryStatus = async (memoryId) => {
  const { data, error } = await supabase
    .from('memories')
    .select(
      'id, title, description, photo_url, figurine_url, figurine_urls, model_3d_url, status, created_at, updated_at'
    )
    .eq('id', memoryId)
    .single();

  if (error) throw error;
  return data;
};

// Check if model_3d_url is available for a memory
export const checkMemory3DStatus = async (memoryId) => {
  const { data, error } = await supabase
    .from('memories')
    .select('id, model_3d_url, status')
    .eq('id', memoryId)
    .single();

  if (error) throw error;
  return data;
};

// Product operations
export const getProductPrice = async (productName = 'Tiniego Figurine') => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('price')
      .eq('name', productName)
      .eq('is_active', true)
      .single();

    if (error) {
      // If specific product not found, try to get the default figurine price
      if (error.code === 'PGRST116') {
        const { data: defaultData, error: defaultError } = await supabase
          .from('products')
          .select('price')
          .eq('category', 'figurine')
          .eq('is_active', true)
          .order('price', { ascending: true })
          .limit(1)
          .single();

        if (defaultError) {
          logger.error('Error fetching default product price:', defaultError);
          return { success: false, error: defaultError.message };
        }

        return { success: true, price: defaultData.price };
      }
      
      logger.error('Error fetching product price:', error);
      return { success: false, error: error.message };
    }

    return { success: true, price: data.price };
  } catch (error) {
    logger.error('Error fetching product price:', error);
    return { success: false, error: error.message };
  }
};


// Rate limit: remaining daily generations for figurine
export const getRemainingFigurineGenerations = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'Missing userId' };
    }

    const action = 'generate_figurine';
    const today = new Date().toISOString().slice(0, 10); // UTC date YYYY-MM-DD

    const overridePromise = supabase
      .from('rate_limits')
      .select('daily_limit')
      .eq('action', action)
      .eq('user_id', userId)
      .maybeSingle();

    const defaultPromise = supabase
      .from('rate_limits')
      .select('daily_limit')
      .eq('action', action)
      .is('user_id', null)
      .single();

    const usagePromise = supabase
      .from('rate_limit_usage')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('action', action)
      .eq('bucket_date', today);

    const [overrideRes, defaultRes, usageRes] = await Promise.all([
      overridePromise,
      defaultPromise,
      usagePromise,
    ]);

    if (defaultRes.error) throw defaultRes.error;
    if (overrideRes.error && overrideRes.error.code !== 'PGRST116') {
      // Ignore no rows (PGRST116). Any other error should surface
      throw overrideRes.error;
    }

    const dailyLimit = overrideRes.data?.daily_limit ?? defaultRes.data?.daily_limit ?? 0;
    const usedCount = usageRes.count ?? 0;
    const remaining = Math.max(0, dailyLimit - usedCount);

    return { success: true, remaining, dailyLimit, used: usedCount };
  } catch (error) {
    logger.error('Error fetching remaining figurine generations:', error);
    return { success: false, error: error.message };
  }
};
